import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Model } from '../../app.model';
import {BuyService, CloudDomain, UpgradeOfferData} from '../../buy/buy.service';
import { Subscription } from '../../subscription/subscription.model';
import { SubscriptionService } from '../../subscription/subscription.service';
import { MessageService } from 'primeng/api';
import { I18NextService } from 'angular-i18next';
import { HttpClient } from '@angular/common/http';
import { ProductPriceDiscount, SlaProduct, Product } from '../../buy/select-product/product.model';
import { CustomerService } from 'src/app/customer/customer.service';
import { AutorunComponent } from '@myshared/autorun.component';
import { Customer } from 'src/app/customer/customer.model';
import { CompanyService } from "../../company/company.service";
import { BankAccount, Company, Contact } from "../../company/company.model";
import { firstValueFrom } from 'rxjs';
import { PaymentMethodCode } from "../../buy/buy.model";
import { observable, runInAction } from "mobx";
import {AuthService} from "../../auth/auth.service";
import {FormEmitter} from "../../forms/form.component";

type BuyUpgradeStep =
  | 'select-contact'
  | 'creating-offer'
  | 'select-upgrade'
  | 'select-sla'
  | 'customer-invoice-address'
  | 'customer-payment-method'
  | 'invoice-address'
  | 'payment-method'
  | 'checkout-summary'
  | 'thank-you-partner'
  | 'thank-you';

function validateBuyStep(input: string, orderName: string): BuyUpgradeStep | boolean {
  if (['select-upgrade', 'select-sla', 'select-contact'].indexOf(input) !== -1) {
    return input as BuyUpgradeStep;
  }
  if (orderName &&
    ['customer-invoice-address', 'customer-payment-method', 'invoice-address', 'payment-method',
      'checkout-summary', 'thank-you', 'thank-you-partner']
      .indexOf(input) !== -1) {
    return input as BuyUpgradeStep;
  }
  return false;
}

@Component({
    selector: 'app-workflow-upgrade-one',
    templateUrl: './workflow-upgrade-one.component.html',
    standalone: false
})
export class WorkflowUpgradeOneComponent extends AutorunComponent implements OnInit {
  public orderName: string;
  public applianceId = '';
  public selectedCu = 0;
  public selectedSLA = '';

  public step: BuyUpgradeStep;
  @observable public subscription: Subscription;

  public slaProducts: SlaProduct[];
  public pcOnePrice: number;
  public pcOneDiscount: ProductPriceDiscount[];
  public selectedCustomer: Customer | Company;
  public selectedContactId: string;
  public selectedDomain: string;
  public isPC20CSP: boolean;
  public paymentMethod: PaymentMethodCode;
  public isPascomApproved: boolean;
  public subscriptionReference: string = '';
  public isAdmin: boolean;

  @observable public company: Company;

  constructor(
    private m: Model,
    private route: ActivatedRoute,
    private router: Router,
    private buyService: BuyService,
    private subscriptionService: SubscriptionService,
    private messageService: MessageService,
    private i18next: I18NextService,
    private http: HttpClient,
    private customerService: CustomerService,
    private companyService: CompanyService,
    private authService: AuthService
  ) {
    super();
    this.m.buy.resetCurrentOrder();
  }

  async ngOnInit() {
    if (this.m.account.currentUser.isInApproval) {
      this.cancelWorkflow();
      return;
    }

    this.autorun(async () => {
      // reload subscription if we have an offer but no subscription
      if (!this.subscription && this.m.buy.currentOrder.name !== '') {
        try {
          await this.getSubscription(+this.m.buy.currentOrder.aid);
        } catch (e) { }
      }

      // Partner offer
      if (this.subscription && !this.subscription.is_my) {
        // check if partner is pascom 20 csp partner
        this.isPC20CSP = this.m.account.currentUser.companyIsPC20Csp;
        if (this.subscription && !this.subscription?.is_my) {
          // find the customer for this subscription
          if (!this.isPC20CSP) {
            this.findAndSelectContact();
            await this.reloadCompany();
          }
        }
      }

      this.paymentMethod = this.m.buy.currentOrder?.payment_method ?? this.subscription?.payment_method;
      this.isPascomApproved = this.m.buy.currentOrder.isPascomApproved;
      this.isAdmin = this.m.account.isMypascomAdmin;
    });

    this.applianceId = this.route.snapshot.queryParamMap.get('aid') ?? undefined

    // Handle url parameter change
    this.route.paramMap.subscribe(async (params: ParamMap) => {
      try {
        if (params.has('id')) {
          const id = params.get('id');
          this.handleOffer(id);
        }

        if (params.has('subroute')) {
          const sr = params.get('subroute');
          if (!this.applianceId)
            this.applianceId = +sr > 0 ? sr : undefined;

          await this.handleSubroute(params);

          const newStep = validateBuyStep(sr, this.orderName ?? '');
          if (newStep !== false) {
            this.setStep(newStep as BuyUpgradeStep);
            return;
          }
        }
      } catch (e) { }
    });
  }

  private handleOffer(id: string) {
    if (id !== '') {
      this.orderName = id;
      this.buyService.refresh(this.orderName);
    }
  }

  private findAndSelectContact() {
    if (this.m.customer.customers.length > 0) {
      this.selectedCustomer = this.m.customer.customers.find(r => r.id === this.subscription?.customer_id);
      if (this.selectedCustomer && this.m.buy.currentOrder.name === '') {
        this.setStep('select-contact');
      }
    }
  }

  private async handleSubroute(params: ParamMap) {
    try {
      if (this.applianceId && this.m.workflow.isOneUpgrade) {
        if (!params.has('id')) {
          this.router.navigate(['/do', this.m.workflow.name, this.applianceId, ''], { queryParamsHandling: 'preserve' });
          return;
        }

        await this.getSubscription(+this.applianceId);
        await this.getProducts();

        if (this.subscription.is_my || this.isPC20CSP) {
          if (this.m.workflow.isOneUpgrade) {
            this.setStep('select-upgrade');
          }
        } else {
          this.getCompany();
        }
      }
    } catch (e) { }
  }

  private getCompany() {
    this.customerService.refresh(true);
  }

  private setStep(step: BuyUpgradeStep) {
    this.step = step;
  }

  private navigateToStep(step: BuyUpgradeStep, orderId?: string, replaceUrl?: boolean) {
    this.router.navigate(['/do', this.m.workflow.name, step, orderId ?? ''], { queryParamsHandling: 'preserve', replaceUrl });
  }

  public onProductSelected(cu: number) {
    this.selectedCu = cu;
    this.buyService.selectedProduct = 'PC-ONE';
    this.setStep('select-sla');
  }

  public onBackUpgradeOne() {
    if (this.subscription.is_my || this.isPC20CSP) {
      this.m.workflow.resetWorkflow();

      this.cancelWorkflow();
      return;
    }

    this.setStep('select-contact');
  }

  public onSLAProductSelected(sla: string) {
    this.selectedSLA = sla;
    this.selectProductAndCreateOffer();
  }

  public onSLAProductBack() {
    this.setStep('select-upgrade');
  }

  /**
   * Partner is filling invoice address for his customer
   * @param company
   */
  public onCustomerInvoiceAddressFilled(company: Company) {
    this.companyService.update(company).subscribe((company: Company) => {
      if (this.isPC20CSP) {
        runInAction(() => { this.company = company; })
      }

      this.navigateToStep('customer-payment-method', this.orderName);
    });
  }

  /**
   * Partner is filling payment details for his customer
   * @param bank
   */
  public async onCustomerPaymentMethodSelected({ method, bank }: { method?: PaymentMethodCode; bank?: BankAccount }) {
    try {
      if (method === 'sepa') {
        bank.partner_id = +this.company.id;
        await firstValueFrom(this.companyService.updateBankAccount(bank));
        this.company.bankAccounts[0] = bank
      }

      this.recalculateOfferCheckoutSummary();
    } catch (e) {}
  }

  public onInvoiceAddressFilled(company: Company) {
    this.navigateToStep('payment-method', this.orderName);
  }

  public onPaymentMethodSelected() {
    this.recalculateOfferCheckoutSummary();
  }

  public async onBuy() {
    if (this.isAdmin && !!this.subscriptionReference) {
      const isReferenceSaved = await this.sendReference();
      if (!isReferenceSaved) return;
    }

    if (this.m.account.currentUser.company_id !== this.m.buy.currentOrder.invoice_address.id && !this.isPC20CSP) {
      this.onBuyPartner();
      return;
    }

    this.onBuyCspOrCustomer();
  }

  public onSubscriptionReferenceChanged(form: FormEmitter<string>) {
    this.subscriptionReference = form.data;
  }

  public async sendReference() {
    try {
      await firstValueFrom(this.subscriptionService.setSubscriptionReference(this.subscription.appliance_id, this.subscriptionReference));
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  public onScreenPascomApprovalNext() {
    window.history.replaceState(null, null, '/subscriptions');
    this.m.workflow.resetWorkflow();

    if (!!this.applianceId) {
      window.location.replace('/subscriptions/'+this.applianceId);
    } else {
      window.location.replace('/subscriptions');
    }
  }

  public onContactSelected(contact: Contact) {
    if (!contact.id) {
      window.location.reload();
      return;
    }
    this.selectedContactId = contact.id;

    this.setStep('select-upgrade');
  }

  public onSelectContactBack() {
    this.cancelWorkflow();
  }

  recalculateOfferCheckoutSummary() {
    if (this.isPC20CSP) {
      this.buyService.getOfferRecalculate(this.orderName).subscribe(r => {
        this.navigateToStep('checkout-summary', this.orderName, true);
      });
    } else {
      this.navigateToStep('checkout-summary', this.orderName, true);
    }
  }

  private async selectProductAndCreateOffer() {
    const product = this.buyService.selectedProduct;
    const slaProduct = this.selectedSLA;
    let cu = null;
    if (!!this.selectedCu && this.selectedCu > 0) {
      cu = this.selectedCu;
    }

    if (this.subscription.isOne && cu === +this.subscription.users
      && slaProduct === this.subscription.sla_product) {
      this.messageService.add({
        severity: 'error',
        summary: this.i18next.t('product_no_upgrade_option_selected') as string,
        detail: this.i18next.t('product_no_upgrade_option_selected_description') as string
      });
      return;
    }

    this.setStep('creating-offer');
    const offer = {
      appliance_id: this.applianceId,
      partner_id: this.selectedContactId ?? undefined,
      sla_product: slaProduct,
      cu
    }

    try {
      await firstValueFrom(this.buyService.createUpgradePascomOneOffer(offer));
      this.orderName = this.m.buy.currentOrder.name;

      this.buyService.changeProduct(product, slaProduct, cu).subscribe({
        next: (r: any) => {
          if (this.subscription.is_my) {
            this.navigateToStep('invoice-address', this.orderName);
          } else {
            this.navigateToStep('customer-invoice-address', this.orderName);
          }
        },
        error: (e: any) => {
          this.cancelWorkflow()
        }
      })
    } catch (e) {
      this.cancelWorkflow();
    }
  }

  public onSummaryBack() {
    if(this.paymentMethod === 'wire_transfer') {
      history.go(-2);
    } else {
      history.back();
    }
  }

  private onBuyPartner() {
    this.buyService.confirmPartnerQuotation().subscribe(confirm => {
      if (this.isPC20CSP) {
        this.navigateToStep('thank-you', confirm.name);
      } else {
        this.navigateToStep('thank-you-partner', confirm.name);
      }
    });
  }

  private onBuyCspOrCustomer() {
    const data: UpgradeOfferData = {
      cspdomain: this.isPC20CSP ? this.selectedDomain : undefined
    }
    this.buyService.confirmUpgrade(data).subscribe(confirm => {
      this.navigateToStep('thank-you', confirm.name);
    });
  }

  private cancelWorkflow() {
    this.m.workflow.resetWorkflow();

    if (this.applianceId) {
      this.router.navigate(['/', 'subscriptions', this.applianceId]);
    } else {
      this.router.navigate(['/', 'subscriptions']);
    }
  }

  private async getSubscription(aid: number) {
    try {
      const subscription = await firstValueFrom(this.subscriptionService.getSubscriptionDetails(aid));
      runInAction(() => {
        this.subscription = Subscription.fromJson(subscription);
      })

      return Promise.resolve(this.subscription);
    } catch (e) {
      this.cancelWorkflow();
    }
  }

  private async reloadCompany() {
    // reload company of the customer
    if (!this.company && this.m.buy.currentOrder.name !== '') {
      let company = this.m.buy.currentOrder.invoice_address;
      if (company.bankAccounts.length <= 0) {
        try {
          company = await firstValueFrom(this.companyService.getCompany(company.id));
        } catch (e) { }
      }
      runInAction(() => {
        this.company = company;
      });
    }
  }

  private async getProducts() {
    const cloudProducts =
        await firstValueFrom(
            this.http.get<{ sla, pc_one_price, pc_one_discount, free, basic, premium }>('/services/products/cloud')
        );

    this.slaProducts = cloudProducts.sla.map(r => SlaProduct.fromJson(r));
    this.pcOnePrice = cloudProducts.pc_one_price || 0;
    this.pcOneDiscount = cloudProducts.pc_one_discount.map(r => ProductPriceDiscount.fromJson(r));
  }

}
